// Module Import
//---------------------------------
import React, { useId, useRef, useState } from 'react'
import Field from '../../../atoms/fields/field'
import TypographyAtom from '../../../atoms/typography/typography'
import useFormValidation from '../../../../hooks/useFormValidation'
import { contactFormFooterSchema } from '../../../../utils/form/validationSchema'
import ValidationWrapper from '../../../atoms/fields/ValidationWrapper'
import { useToast } from '../../../../context/ToastContext'
import ButtonAtom from '../../../atoms/buttons/buttons'
import {
  BodyInner,
  BodyWrapper,
  ConsentFieldOuter,
  EmailFieldGrow,
  FieldWrapper,
  Form,
  FormFieldsColumn,
  InputButtonRow,
  NewsletterHeading,
  PrivacyPolicyLink,
} from './ContactFormFooter.styles'
import axios from 'axios'
import { identifyKlaviyo } from '../../../../utils/klaviyo/klaviyoOnsite'

const newsletterConsentLabelContent = (
  <>
    I agree to receive industry news, case studies, offers & promotions, training & events, product
    updates, and marketing emails from heliguy™. I understand I can unsubscribe at any time. By
    subscribing, I agree to heliguy’s{' '}
    <PrivacyPolicyLink to="/support/privacy-policy/">Privacy Policy</PrivacyPolicyLink>.
  </>
)

const MARKETING_CONSENT_REMINDER = 'Please confirm that you agree to receive marketing emails.'

// Component Export
//---------------------------------
export default function ContactFormFooter({ modalMode = false }) {
  const instanceSuffix = useId().replace(/:/g, '')
  const formDomId = `contactFormFooter-${instanceSuffix}`
  const contactEmailId = `contactEmail-${instanceSuffix}`
  const newsletterMarketingConsentId = `newsletterMarketingConsent-${instanceSuffix}`

  const [isSent, setIsSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [marketingConsent, setMarketingConsent] = useState(false)
  const formRef = useRef(null)

  const { showToast } = useToast()
  const { validateValues, validateFieldOnChange, errors } = useFormValidation({
    schema: contactFormFooterSchema,
    formRef,
  })

  const messageSentConfirmation = 'You have successfully subscribed to our newsletter!'

  const handleFieldClick = () => setIsSent(false)

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target
    handleFieldClick()

    if (name === 'email') {
      setEmail(value)
      await validateFieldOnChange({
        name: 'email',
        value,
        comparedValue: marketingConsent,
        compareFieldName: 'marketingConsent',
      })
      return
    }

    if (name === 'marketingConsent' && type === 'checkbox') {
      const next = Boolean(checked)
      setMarketingConsent(next)
      await validateFieldOnChange({
        name: 'marketingConsent',
        value: next,
        comparedValue: email,
        compareFieldName: 'email',
      })
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    if (!marketingConsent) {
      showToast('Reminder', MARKETING_CONSENT_REMINDER)
      setIsLoading(false)
      return
    }

    const formData = new FormData(e.target)
    formData.delete('marketingConsent')

    const formValues = {
      email: (formData.get('email') || '').toString().trim(),
      marketingConsent,
    }

    const validatedData = await validateValues(formValues)

    if (!validatedData) {
      if (!marketingConsent) {
        showToast('Reminder', MARKETING_CONSENT_REMINDER)
      } else {
        showToast('Validation error', 'Email is required')
      }
      setIsLoading(false)
      return
    }

    const response = await axios
      .post(`${process.env.GATSBY_NETLIFY_API_URL}/mail_subscribe`, formData)
      .catch((err) => {
        showToast('Unsuccessfully', err?.response?.data?.error || 'Failed to subscribe')
        return null
      })

    if (response) {
      identifyKlaviyo({ email: formValues.email })
      setIsSent(true)
      setEmail('')
      setMarketingConsent(false)
      e.target.reset()
    }

    setIsLoading(false)
  }

  return (
    <Form ref={formRef} id={formDomId} onSubmit={onSubmit}>
      {isSent ? (
        <TypographyAtom weight="Bold" type="H4" copy={messageSentConfirmation} />
      ) : (
        <BodyWrapper>
          {!modalMode && <NewsletterHeading>Sign up to our newsletter</NewsletterHeading>}
          <BodyInner>
            <FormFieldsColumn>
              <InputButtonRow>
                <EmailFieldGrow>
                  <FieldWrapper>
                    <ValidationWrapper errorMessage={errors.email}>
                      <Field
                        labelText="Contact Email"
                        labelFor={contactEmailId}
                        placeholderText="Contact Email*"
                        fieldType="email"
                        fieldId={contactEmailId}
                        fieldName="email"
                        fullWidth
                        value={email}
                        onChange={handleChange}
                        onClick={handleFieldClick}
                      />
                    </ValidationWrapper>
                  </FieldWrapper>
                </EmailFieldGrow>
                <ButtonAtom
                  type="submit"
                  fullWidth={modalMode}
                  buttonType="Primary Button"
                  buttonCopy="Join our newsletter"
                  form={formDomId}
                  isLoading={isLoading}
                  disabled={isLoading}
                />
              </InputButtonRow>
              <ConsentFieldOuter>
                <ValidationWrapper errorMessage={errors.marketingConsent}>
                  <Field
                    float
                    labelContent={newsletterConsentLabelContent}
                    labelText=""
                    fieldType="checkbox"
                    labelFor={newsletterMarketingConsentId}
                    fieldId={newsletterMarketingConsentId}
                    fieldName="marketingConsent"
                    placeholderText=""
                    disabled={isLoading}
                    checked={marketingConsent}
                    onChange={handleChange}
                  />
                </ValidationWrapper>
              </ConsentFieldOuter>
            </FormFieldsColumn>
          </BodyInner>
        </BodyWrapper>
      )}
    </Form>
  )
}

ContactFormFooter.propTypes = {}
